import { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { useTranslation } from 'react-i18next';
import { analyticsService } from '../services/api';
import { Briefcase, Mail, MessageSquare, Trophy } from 'lucide-react';
import SkeletonLoader from '../components/ui/SkeletonLoader';

function AnalyticsPage() {
    const { t } = useTranslation();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    useEffect(() => {
        analyticsService.getDashboard()
            .then(res => setData(res.data))
            .catch(() => setError(t('common.error')))
            .finally(() => setLoading(false));
    }, []);

    const getScoreColor = (score) => {
        if (score >= 70) return '#10b981';
        if (score >= 50) return '#f59e0b';
        return '#ef4444';
    };

    const STATUS_COLORS = {
        applied: '#3b82f6',
        interview: '#f59e0b',
        offer: '#10b981',
        rejected: '#ef4444',
        withdrawn: '#6b7280',
    };

    const statusLabel = (status) => t(`internships.${status}`);

    if (loading) return (
        <Layout>
            <div className="page-container"><SkeletonLoader /></div>
        </Layout>
    );

    if (error) return (
        <Layout>
            <div className="page-container">
                <div className="error-message">{error}</div>
            </div>
        </Layout>
    );

    const total = data.total_applications;

    return (
        <Layout>
            <div className="page-container">
                <h1>{t('analytics.title')}</h1>
                <p className="subtitle">{t('analytics.subtitle')}</p>

                <div className="dashboard-stats">
                    <div className="dash-stat-card">
                        <div className="dash-stat-icon" style={{ background: '#dbeafe' }}>
                            <Briefcase size={20} color="#3b82f6" />
                        </div>
                        <div><h3>{total}</h3><p>{t('dashboard.total_applications')}</p></div>
                    </div>
                    <div className="dash-stat-card">
                        <div className="dash-stat-icon" style={{ background: '#dcfce7' }}>
                            <Mail size={20} color="#10b981" />
                        </div>
                        <div>
                            <h3 style={{ color: '#10b981' }}>{data.response_rate}%</h3>
                            <p>{t('dashboard.response_rate')}</p>
                        </div>
                    </div>
                    <div className="dash-stat-card">
                        <div className="dash-stat-icon" style={{ background: '#fef3c7' }}>
                            <MessageSquare size={20} color="#f59e0b" />
                        </div>
                        <div>
                            <h3 style={{ color: '#f59e0b' }}>{data.interview_conversion_rate}%</h3>
                            <p>{t('dashboard.interview_rate')}</p>
                        </div>
                    </div>
                    <div className="dash-stat-card">
                        <div className="dash-stat-icon" style={{ background: '#f3e8ff' }}>
                            <Trophy size={20} color="#8b5cf6" />
                        </div>
                        <div>
                            <h3 style={{ color: '#8b5cf6' }}>{data.offer_rate}%</h3>
                            <p>{t('dashboard.offer_rate')}</p>
                        </div>
                    </div>
                </div>

                <div className="analytics-grid">
                    <div className="card">
                        <h2>{t('analytics.by_status')}</h2>
                        <div className="status-breakdown">
                            {Object.entries(data.status_breakdown).map(([status, count]) => (
                                <div key={status} className="status-bar-row">
                                    <div className="status-bar-label">
                                        <span className="status-dot" style={{ background: STATUS_COLORS[status] }} />
                                        <span className="status-name">{statusLabel(status)}</span>
                                        <span className="status-count">{count}</span>
                                    </div>
                                    <div className="status-bar-track">
                                        <div
                                            className="status-bar-fill"
                                            style={{
                                                width: total > 0 ? `${(count / total) * 100}%` : '0%',
                                                background: STATUS_COLORS[status],
                                            }}
                                        />
                                    </div>
                                    <span className="status-pct">
                                        {total > 0 ? Math.round((count / total) * 100) : 0}%
                                    </span>
                                </div>
                            ))}
                        </div>
                    </div>

                    {data.resume_stats && (
                        <div className="card">
                            <h2>{t('analytics.resume_performance')}</h2>
                            <div className="resume-analytics">
                                <p className="resume-count">
                                    {data.resume_stats.total_resumes} {t('analytics.analysed')}
                                </p>
                                <div className="analytics-score-item">
                                    <div className="analytics-score-label">
                                        <span>{t('analytics.avg_ats')}</span>
                                        <strong style={{ color: getScoreColor(data.resume_stats.avg_ats_score) }}>
                                            {data.resume_stats.avg_ats_score}%
                                        </strong>
                                    </div>
                                    <div className="analytics-score-bar">
                                        <div className="analytics-score-fill" style={{
                                            width: `${data.resume_stats.avg_ats_score}%`,
                                            background: getScoreColor(data.resume_stats.avg_ats_score)
                                        }} />
                                    </div>
                                </div>
                                <div className="analytics-score-item">
                                    <div className="analytics-score-label">
                                        <span>{t('analytics.avg_keyword')}</span>
                                        <strong style={{ color: getScoreColor(data.resume_stats.avg_keyword_score) }}>
                                            {data.resume_stats.avg_keyword_score}%
                                        </strong>
                                    </div>
                                    <div className="analytics-score-bar">
                                        <div className="analytics-score-fill" style={{
                                            width: `${data.resume_stats.avg_keyword_score}%`,
                                            background: getScoreColor(data.resume_stats.avg_keyword_score)
                                        }} />
                                    </div>
                                </div>
                                <div className="analytics-score-item">
                                    <div className="analytics-score-label">
                                        <span>{t('analytics.avg_readability')}</span>
                                        <strong style={{ color: getScoreColor(data.resume_stats.avg_readability_score) }}>
                                            {data.resume_stats.avg_readability_score}%
                                        </strong>
                                    </div>
                                    <div className="analytics-score-bar">
                                        <div className="analytics-score-fill" style={{
                                            width: `${data.resume_stats.avg_readability_score}%`,
                                            background: getScoreColor(data.resume_stats.avg_readability_score)
                                        }} />
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {data.top_companies?.length > 0 && (
                        <div className="card">
                            <h2>{t('analytics.top_companies')}</h2>
                            <div className="top-companies">
                                {data.top_companies.map((company, i) => (
                                    <div key={i} className="company-row">
                                        <div className="company-rank">{i + 1}</div>
                                        <div className="company-name-cell">{company.company_name}</div>
                                        <div className="company-count-badge">
                                            {company.count} {company.count !== 1
                                                ? t('analytics.applications')
                                                : t('analytics.application')}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {data.applications_over_time?.length > 0 && (
                        <div className="card">
                            <h2>{t('analytics.recent_activity')}</h2>
                            <div className="activity-list">
                                {data.applications_over_time.map((item, i) => (
                                    <div key={i} className="activity-row">
                                        <span className="activity-date">{item.date}</span>
                                        <div className="activity-bar-track">
                                            <div className="activity-bar-fill"
                                                style={{ width: `${Math.min(item.count * 20, 100)}%` }} />
                                        </div>
                                        <span className="activity-count">
                                            {item.count} {item.count !== 1
                                                ? t('analytics.applications')
                                                : t('analytics.application')}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>

                {total === 0 && (
                    <div className="card" style={{ textAlign: 'center', padding: '3rem' }}>
                        <p style={{ fontSize: '3rem' }}>📊</p>
                        <h2 style={{ marginTop: '1rem' }}>{t('analytics.no_data')}</h2>
                        <p className="subtitle">{t('analytics.no_data_subtitle')}</p>
                        <a href="/internships" className="btn-primary"
                            style={{ display: 'inline-block', marginTop: '1rem', textDecoration: 'none' }}>
                            {t('analytics.add_first')}
                        </a>
                    </div>
                )}
            </div>
        </Layout>
    );
}

export default AnalyticsPage;